import { checkExhaustive } from '../../common/asserts';
import * as corgi from '../../corgi';
import { VElementOrPrimitive } from '../../corgi/vdom';
import { ACTION, CHANGED, PRESSED } from '../events';
import { Input } from '../input';

import {
  Args,
  InfoMenuItem,
  MenuController,
  MenuEntries,
  MenuEntry,
  State,
} from './menu_controller';

// Below this a menu opens as a sheet along the bottom, because a phone has no hover to open
// submenus and no room to put them beside their parents. 640 is Tailwind's sm breakpoint.
const SHEET_MAX_WIDTH_PX = 640;

export interface Position {
  x: number;
  y: number;
}

export interface MenuClassNames {
  // Applied to the popup container (and the search-mode container).
  popup?: string;
  // Applied to each item entry. State exposed via data attributes:
  // data-active, data-accent, data-disabled — use Tailwind data-[…] variants.
  item?: string;
  divider?: string;
  // Applied to a menu item's detail line
  detail?: string;
  // Applied to the column icons and checkmarks sit in, which is 16px wide with nothing after it
  icon?: string;
  // Applied to each info row in place of item, since an info row is never active or disabled
  info?: string;
}

export function MenuElement(
  {
    accent,
    anchor,
    bound,
    classes,
    items,
    position,
    trigger,
  }: {
    accent: number[];
    anchor: 'first_item' | 'top_left';
    bound: DOMRect;
    classes: MenuClassNames;
    items: MenuEntries;
    position: Position;
    trigger?: Element;
  },
  state: State | undefined,
  updateState: (newState: State) => void,
) {
  if (!state) {
    const size = estimateSize(items);
    const xFraction = size[0] / bound.width;
    const yFraction = size[1] / bound.height;
    let mode: State['mode'];
    if (bound.width < SHEET_MAX_WIDTH_PX) {
      mode = 'sheet';
    } else if (xFraction < 0.75 && yFraction < 0.75) {
      mode = 'normal';
    } else {
      mode = 'searching';
    }
    state = {
      active: [0],
      items,
      mode,
      opened: [],
      search: '',
      searchIndex: -1,
      searchResults: [],
    };
  }

  let anchored: [number, number];
  if (anchor === 'first_item') {
    anchored = [position.x - 48, position.y - 16];
  } else if (anchor === 'top_left') {
    anchored = [position.x, position.y + 4];
  } else {
    throw checkExhaustive(anchor);
  }

  const args: Args = {items, trigger};

  // Only a sheet's backdrop takes pointer events, so that outside a popup the page keeps its hover,
  // scrolling, and clicks while the menu is open. MenuController closes on presses outside.
  return (
    <div
      className={
        state.mode === 'sheet' ? 'absolute inset-0' : 'absolute inset-0 pointer-events-none'
      }
      js={corgi.bind({
        args,
        controller: MenuController,
        events: {
          contextmenu: 'maybeClickClose',
          click: 'maybeClickClose',
          render: 'wakeup',
        },
        key: `${anchored[0]},${anchored[1]}`,
        state: [state, updateState],
      })}
    >
      {state.mode === 'normal' ? (
        <NormalMenu
          accent={accent}
          anchor={anchored}
          bound={bound}
          classes={classes}
          state={state}
        />
      ) : (
        <></>
      )}
      {state.mode === 'searching' ? (
        <SearchMenu anchor={anchored} classes={classes} state={state} />
      ) : (
        <></>
      )}
      {state.mode === 'sheet' ? (
        <SheetMenu classes={classes} state={state} />
      ) : (
        <></>
      )}
    </div>
  );
}

function NormalMenu({
  accent,
  anchor,
  bound,
  classes,
  state,
}: {
  accent: number[];
  anchor: [x: number, y: number];
  bound: DOMRect;
  classes: MenuClassNames;
  state: State;
}) {
  const active = state.active;
  const items = state.items;
  const size = estimateSize(items);
  const newAnchor = [
    Math.max(0, Math.min(anchor[0], bound.width - size[0])),
    Math.max(0, Math.min(anchor[1], bound.height - size[1])),
  ];
  return (
    <div
      className="absolute"
      style={`left: ${newAnchor[0]}px; top: ${newAnchor[1]}px`}
    >
      <SubMenu
        accent={accent}
        active={active}
        boundWidth={bound.width}
        classes={classes}
        flip={false}
        items={items}
        leftPx={newAnchor[0]}
      />
    </div>
  );
}

function SubMenu({
  accent,
  active,
  boundWidth,
  classes,
  flip,
  items,
  leftPx,
}: {
  accent: number[];
  active: number[];
  boundWidth: number;
  classes: MenuClassNames;
  flip: boolean;
  items: MenuEntries;
  leftPx: number;
}) {
  const width = estimateSize(items)[0];
  const rightPx = leftPx + width;
  const hasIcons = items.some(itemHasIcon);
  // The popup's containing block is the zero width div NormalMenu positions, so without an
  // intrinsic width it shrinks to fit nothing and wraps every label. estimateSize measures labels
  // unwrapped, so the clamping below assumes this too.
  const base =
    `absolute -top-1 pointer-events-auto w-max z-50 ${flip ? 'end-full' : 'start-full'}`;
  const cls = classes.popup ? `${base} ${classes.popup}` : base;
  return (
    <div className={cls} data={{menuPopup: ''}}>
      {items.map((item, i) => (
        <Entry
          accent={accent}
          active={active}
          boundWidth={boundWidth}
          classes={classes}
          hasIcons={hasIcons}
          index={i}
          item={item}
          parentLeft={leftPx}
          parentRight={rightPx}
        />
      ))}
    </div>
  );
}

function Entry({
  accent,
  active,
  boundWidth,
  classes,
  hasIcons,
  index,
  item,
  parentLeft,
  parentRight,
}: {
  accent: number[];
  active: number[];
  boundWidth: number;
  classes: MenuClassNames;
  hasIcons: boolean;
  index: number;
  item: MenuEntry;
  parentLeft: number;
  parentRight: number;
}) {
  const isAccent = accent[0] === index;
  const isActive = active[0] === index;
  const lastActive = active.length === 1;
  const disabled =
    (item.kind === 'menu' ||
      item.kind === 'menu_item' ||
      item.kind === 'checkbox_menu_item') &&
    !!item.disabled;

  // Three-column row: icon | label | trailing. The icon column is only
  // reserved when at least one sibling actually has one, so menus without
  // icons or checkboxes don't pay for an empty gutter. Trailing uses
  // ms-auto to sit flush right when the popup is forced wider by a
  // sibling, with ps-4 providing a fixed minimum gap.
  const iconSlot = hasIcons
    ? <div className={iconClass(classes)}>{iconContent(item)}</div>
    : null;
  const baseItem = 'outline-none relative';
  if (item.kind === 'info_menu_item') {
    return <InfoRow base={baseItem} classes={classes} iconSlot={iconSlot} item={item} />;
  }

  let element;
  if (item.kind === 'menu_item') {
    element = (
      <div className="flex items-center">
        {iconSlot}
        <Label classes={classes} item={item} />
        {item.shortcut ? (
          <div className="ms-auto ps-4">{item.shortcut}</div>
        ) : null}
      </div>
    );
  } else if (item.kind === 'checkbox_menu_item') {
    element = (
      <div className="flex items-center">
        {iconSlot}
        <div>{item.label}</div>
        {item.shortcut ? (
          <div className="ms-auto ps-4">{item.shortcut}</div>
        ) : null}
      </div>
    );
  } else if (item.kind === 'menu') {
    const childWidth = estimateSize(item.items)[0];
    const flipChild = parentRight + childWidth > boundWidth;
    const childLeft = flipChild ? parentLeft - childWidth : parentRight;
    element = (
      <>
        <div className="flex items-center">
          {iconSlot}
          <div>{item.label}</div>
          <div className="ms-auto ps-4">›</div>
        </div>
        {isActive && !disabled ? (
          <SubMenu
            accent={isAccent ? accent.slice(1) : []}
            active={active.slice(1)}
            boundWidth={boundWidth}
            classes={classes}
            flip={flipChild}
            items={item.items}
            leftPx={childLeft}
          />
        ) : (
          <></>
        )}
      </>
    );
  } else if (item.kind === 'divider') {
    element = <div className={classes.divider ?? ''} />;
  } else {
    throw checkExhaustive(item);
  }

  const data: {[k: string]: string} = {index: String(index)};
  if (disabled) data.disabled = '';
  if (isAccent) data.accent = '';
  if (isActive) data.active = '';

  const itemClass =
    item.kind === 'divider'
      ? baseItem
      : classes.item
        ? `${baseItem} ${classes.item}`
        : baseItem;

  return (
    <div
      autofocus={isActive && lastActive}
      className={itemClass}
      data={data}
      tabindex="0"
      unboundEvents={{
        click: 'selected',
        focus: 'focused',
        keyup: 'keyPressed',
        pointerenter: 'pointerEntered',
      }}
    >
      {element}
    </div>
  );
}

function InfoRow({
  base,
  classes,
  iconSlot,
  item,
}: {
  base: string;
  classes: MenuClassNames;
  iconSlot: VElementOrPrimitive | null;
  item: InfoMenuItem;
}) {
  return (
    <div className={classes.info ? `${base} ${classes.info}` : base}>
      <div className="flex items-center">
        {iconSlot}
        <div>{item.label}</div>
        {item.value !== undefined ? (
          <div className="ms-auto ps-4">{item.value}</div>
        ) : null}
      </div>
    </div>
  );
}

function Label({classes, item}: {classes: MenuClassNames; item: MenuEntry}) {
  if (item.kind === 'divider') {
    return <></>;
  } else if (item.kind === 'menu_item' && item.detail !== undefined) {
    return (
      <div className="flex flex-col">
        <div>{item.label}</div>
        <div className={classes.detail ?? ''}>{item.detail}</div>
      </div>
    );
  } else {
    return <div>{item.label}</div>;
  }
}

// Local extents only — submenus open beside their parent, so they don't
// contribute to the top-level popup's footprint (and including them
// over-clamps it leftward when the menu is opened near the right edge).
function estimateSize(items: MenuEntries): [width: number, height: number] {
  const iconWidth = items.some(itemHasIcon) ? 16 : 0;
  let width = 0;
  let height = 4;
  for (const item of items) {
    if (item.kind === 'divider') {
      height += 4;
      continue;
    }
    // An info row's value is an element we can't measure, so we guess it's about 8 characters.
    const extra = item.kind === 'info_menu_item' && item.value !== undefined ? 64 : 0;
    const detail = item.kind === 'menu_item' ? item.detail : undefined;
    const characters = Math.max(item.label.length, detail?.length ?? 0);
    width = Math.max(width, iconWidth + 8 * characters + extra + 32 + 8);
    height += detail !== undefined ? 40 : 24;
  }
  return [width, height];
}

function itemHasIcon(item: MenuEntry): boolean {
  if (item.kind === 'divider') return false;
  if (item.kind === 'checkbox_menu_item') return true;
  return item.icon !== undefined;
}

function iconClass(classes: MenuClassNames): string {
  return classes.icon ? `shrink-0 w-4 ${classes.icon}` : 'shrink-0 w-4';
}

function iconContent(item: MenuEntry): VElementOrPrimitive {
  if (item.kind === 'checkbox_menu_item') return item.checked ? '✓' : '';
  if (item.kind === 'menu_item' || item.kind === 'menu' || item.kind === 'info_menu_item') {
    return item.icon ?? '';
  }
  return '';
}

function SheetMenu({classes, state}: {classes: MenuClassNames; state: State}) {
  let items = state.items;
  let title = undefined;
  for (const index of state.opened) {
    const menu = items[index];
    if (menu.kind !== 'menu') {
      break;
    }
    items = menu.items;
    title = menu.label;
  }
  const hasIcons = items.some(itemHasIcon);
  // 44px is the smallest comfortable touch target
  const base = 'flex flex-col justify-center min-h-11 outline-none';
  const itemClass = classes.item ? `${base} ${classes.item}` : base;

  // The backdrop is a sibling of the sheet so that maybeClickClose, which only closes on clicks
  // that land on the root, still sees taps outside the sheet.
  const sheet = 'absolute bottom-0 inset-x-0 max-h-[70%] overflow-y-auto z-50';
  return (
    <>
      <div className="absolute bg-black/30 inset-0 pointer-events-none z-40" />
      <div
        className={classes.popup ? `${sheet} ${classes.popup}` : sheet}
        style="border-bottom-left-radius: 0; border-bottom-right-radius: 0"
      >
        {title !== undefined ? (
          <div
            className={itemClass}
            unboundEvents={{click: 'sheetBack'}}
          >
            <div className="flex items-center">
              <div className="w-4 shrink-0">‹</div>
              <div className="font-bold">{title}</div>
            </div>
          </div>
        ) : (
          <></>
        )}
        {items.map((item, i) => {
          if (item.kind === 'divider') {
            return <div className={classes.divider ?? ''} />;
          }

          const iconSlot = hasIcons
            ? <div className={iconClass(classes)}>{iconContent(item)}</div>
            : null;
          if (item.kind === 'info_menu_item') {
            return <InfoRow base={base} classes={classes} iconSlot={iconSlot} item={item} />;
          }

          const data: {[k: string]: string} = {index: String(i)};
          if (item.disabled) data.disabled = '';
          return (
            <div
              className={itemClass}
              data={data}
              unboundEvents={{click: 'sheetSelected'}}
            >
              <div className="flex items-center">
                {iconSlot}
                <Label classes={classes} item={item} />
                {item.kind === 'menu' ? <div className="ms-auto ps-4">›</div> : null}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function SearchMenu({
  anchor,
  classes,
  state,
}: {
  anchor: [x: number, y: number];
  classes: MenuClassNames;
  state: State;
}) {
  const base =
    'absolute start-full -top-1 pointer-events-auto w-max z-50 max-h-72 overflow-y-auto';
  const cls = classes.popup ? `${base} ${classes.popup}` : base;
  return (
    <div
      className={cls}
      data={{menuPopup: ''}}
      style={`left: ${anchor[0]}px; top: ${anchor[1]}px`}
    >
      <Input
        autofocus
        unboundEvents={{
          corgi: [
            [ACTION, 'searchAction'],
            [CHANGED, 'searchChanged'],
            [PRESSED, 'searchPressed'],
          ],
        }}
        value={state.search}
      />
      {state.searchResults.map((r, i) => {
        const baseRow = 'outline-none relative';
        const rowCls = classes.item
          ? `${baseRow} ${classes.item}`
          : baseRow;
        const data: {[k: string]: string} = {i: String(i)};
        if (state.searchIndex === i) data.active = '';
        return (
          <div className={rowCls} data={data} unboundEvents={{
            click: 'searchAction',
            pointerenter: 'searchPointerEntered',
          }}>
            <span>
              {r.context.join(' ▸ ')}
              {r.context.length > 0 ? ' ▸ ' : ''}
            </span>
            {r.item.label}
          </div>
        );
      })}
    </div>
  );
}
